import * as React from "react";
import styled from "@emotion/styled";
import Table, { TableRowProps } from "@/components/molecules/Table";
import PropertyType, { toLabel } from "@/constants/PropertyType";
import { toIncludeJapaneseCalendar } from "@/utils/date";
import breakpoint from "@/utils/breakpoint";
import form, { defaultIfBlank, FormValues } from "@/utils/form";
import MiniTitle from "@/components/atoms/MiniTitle";
import Title from "@/components/atoms/Title";
import Modal from "@/components/molecules/Modal";
import MediaQueryContext, { initMediaQueryStore } from "@/contexts/MediaQuery";
import FormButton from "@/components/molecules/FormButton";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import FormContext, { initFormStore } from "@/contexts/Form";
import contract, {
  CONTACT_PREFERENCE_POSITION,
  ContactPreferenceExtras,
  Feature,
  getSentence,
  HIDDEN_COMPONENT_OPTIONS,
  isHiddenComponent,
} from "@/utils/contract";
import PrevButton from "@/components/molecules/PrevButton";
import FormBlock from "@/components/molecules/FormBlock";
import { useFormHandler } from "@/utils/hooks";
import FormContainer from "@/components/atoms/FormContainer";
import { useTheme } from "@/hooks/useTheme";
import { usePrivacyPolicyLabelText } from "@/hooks/usePrivacyPolicyLabelText";
import classNames from "classnames";
import messages from "@/messages";
import { PrefectureEnum } from "@/constants/Prefecture";
import { getShopById } from "@/constants/Shop";
import { getReferralSourceById } from "@/constants/ReferralSource";

type ConfirmModalProps = {
  open: boolean;
  values: FormValues;
  onClose: () => void;
  onValuation: () => void;
};

const ModalHeader = styled(Modal.Header)`
  border: none;
  opacity: 1;
  padding: 0 0 8px 0;
  margin-bottom: 25px;
  border-bottom: 2px solid #2a7593;
  ${breakpoint.lessThan("sm")`
    margin-bottom: 10px;
  `}

  &.override {
    min-height: 0;
    padding: 0;
    border: none;
    margin-bottom: 0;
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 0;
  border: none;
  max-height: 100%;

  ${breakpoint.lessThan("sm")`
    max-height: 100%;
  `}
`;

const HeaderText = styled.h4`
  width: 100%;
  font-size: 17px;
  font-weight: bold;
  line-height: 35px;
  letter-spacing: 0px;
  color: #524939;
  opacity: 1;
  margin-bottom: 10px;

  ${breakpoint.lessThan("sm")`
    font-size: 14px;
    font-weight: bold;
    line-height: 35px;
  `}
`;

const StyledHeaderText = styled.div`
  font-size: 14px;
  letter-spacing: 0px;
  color: #333333;
  opacity: 1;
  font-weight: 400;
  ${breakpoint.lessThan("sm")`
    font-size: 14px;
  `}
`;

const HeaderSubText = styled.h5`
  width: 100%;
  font-size: 17px;
  line-height: 35px;
  letter-spacing: 0px;
  color: #524939;
  opacity: 1;

  ${breakpoint.lessThan("sm")`
    font-size: 11px;
    line-height: 21px;
  `}
`;

const ModalFooter = styled(Modal.Footer)`
  padding: 0;
  border: none;
  margin-top: 50px;
`;

const ModalFooter2 = styled(Modal.Footer)`
  padding: 0;
`;

const FooterText = styled.h4`
  width: 100%;
  text-align: left;
  margin-bottom: 10px;
  font-size: 17px;
  font-weight: bold;
  line-height: 35px;
  border-bottom: 2px solid #2a7593;
  letter-spacing: 0px;
  color: #524939;
  opacity: 1;

  ${breakpoint.lessThan("sm")`
    font-size: 14px;
    font-weight: bold;
    line-height: 35px;
  `}
`;

const StyledDiv = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #333333;
  text-align: center;
`;

const FooterTerms = styled.p`
  width: 100%;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  letter-spacing: 0px;
  opacity: 1;
  margin-bottom: 10px;

  ${breakpoint.lessThan("sm")`
    font-size: 14px;
    font-weight: bold;
    line-height: 35px;
    line-height: 1.5;
  `}
`;

const FooterPolicy = styled.p`
  width: 100%;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  letter-spacing: 0px;
  opacity: 1;
  margin-bottom: 15px;

  ${breakpoint.lessThan("sm")`
    font-size: 14px;
    font-weight: bold;
    line-height: 35px;
    line-height: 1.5;
  `}
`;

const FooterCheckboxArea = styled(FormContainer)`
  margin: 49px 0 10px 0;
  border-bottom: none;
`;

const StyledLink = styled.a<{ color?: string }>`
  color: ${({ color = "#43A0DF" }) => color};
  font-size: 12px;
`;

const Link = styled.a`
  color: #0000ee;
`;

export const TableParent = styled(Table)`
  margin: 0;
`;

const TableRow = styled(Table.Row)`
  ${breakpoint.lessThan("sm")`
    width: 100%;
    display: flex;
    flex-direction : column;
    align-items: center;
  `}
`;

const TableHeader = styled(Table.Cell)<{ width?: string }>`
  width: ${({ width = "260px" }) => width};
  height: 24px;
  font-size: 17px;
  font-weight: bold;
  line-height: 35px;
  background-color: #f2f2f2;
  border: 1px solid #c2c2c2;
  border-right: none;
  color: #524939;
  opacity: 1;
  text-align: left;
  padding: 12px 20px;
  vertical-align: top;

  ${breakpoint.lessThan("sm")`
    width: 100%;
    height: 100%;
    min-height: 38px;
    border: none;
    border: 1px solid #c2c2c2;
    padding: 0;
    padding-left: 10px;
    vertical-align: middle;
  `}

  &.override {
    color: #333333;
    padding: 15px 30px;
    border-left: none;
    background-color: #f7f7f7;
    font-size: 14px;
    font-weight: 500;
    ${breakpoint.lessThan("sm")`
    border: none;
    line-height: 38px;
    padding: 0 10px;
    `}
  }
`;

const TableData = styled(Table.Cell)`
  width: 715px;
  height: 24px;
  font-size: 17px;
  font-weight: bold;
  line-height: 35px;
  color: #524939;
  border: 1px solid #c2c2c2;
  border-left: none;
  opacity: 1;
  text-align: left;
  padding: 12px 20px;

  ${breakpoint.lessThan("sm")`
    width: 100%;
    height: 100%;
    min-height: 38px;
    border: none;
    border: 1px solid #c2c2c2;
    padding: 0;
    padding-left: 10px;
    vertical-align: middle;
  `}
  &.override {
    color: #333333;
    padding: 15px 30px;
    border-right: none;
    font-size: 14px;
    font-weight: 400;
    line-height: 27px;
    ${breakpoint.lessThan("sm")`
     border: none;
     line-height: 38px;
     padding: 0 10px;
    `}
  }
`;

const List = styled.ul`
  list-style: none;
`;

const ListItem = styled.li`
  padding: 0px;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0 0 15px;
  flex-wrap: wrap;
  align-items: center;

  ${breakpoint.lessThan("sm")`
    flex-direction: column;
  `}
  &.marginTop15px {
    margin-top: 15px;
    margin-bottom: 13px;
  }
`;

const ValuationExecButton = styled(FormButton)`
  font-size: 12px;
  font-weight: normal;
  margin: 0 10px;
  flex: 270px;
  line-height: 2em;
  box-shadow: 0 -5px 0 0 #c25c09 inset;

  ${breakpoint.lessThan("sm")`
    font-size: 10px;
    line-height: 1.7;
    flex: none;
  `}
`;

const ValuationButtonText = styled.span`
  font-size: 1.5em;
  font-weight: bold;
  display: block;
  margin: 0;

  ${breakpoint.lessThan("sm")`
    font-size: 1.5em;
    margin: 0;
  `}
`;

const StyledValuationExecButton = styled.button<{ background?: string }>`
  width: 368px;
  height: 70px;
  background: ${({ background = "#43a0df" }) => background};
  border-radius: 35px;
  opacity: 1;
  border: none;
  cursor: pointer;
  ${breakpoint.lessThan("sm")`
  width: 100%;
`}
`;

const StyledValuationText = styled.div`
  text-align: center;
  font-weight: 500;
  letter-spacing: 0px;
  color: #ffffff;
  font-size: 12px;
`;

const StyledValuationTextSub = styled.div`
  text-align: center;
  font-weight: 700;
  letter-spacing: 0px;
  color: #ffffff;
  font-size: 20px;
`;

const StyledReturnButton = styled.button<{ isNewTheme?: boolean }>`
  width: 226px;
  height: 44px;
  font-size: 14px;
  font-weight: 700;
  color: #333333;
  border: 1px solid #333333;
  background-color: #ffffff;
  border-radius: 24px;
  opacity: 1;
  font-weight: bold;
  margin-top: 30px;
  cursor: pointer;
`;

const PcBreak = styled.br`
  display: block;
  ${breakpoint.lessThan("sm")`
    display: none;
  `}
`;

const NoWrap = styled.p`
  margin: -5px 0 0 0;
  white-space: nowrap;
  text-align: center;
`;

const Annotation = styled.p`
  white-space: pre-wrap;
  font-size: 14px;
  font-weight: bold;
  margin-top: 5px;
  &.override {
    line-height: 1.542;
    margin-top: 20px;
    color: #333333;
    font-size: 12px;
    font-weight: 400;
  }
`;

const PolicyArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin: 30px 0 0 0;
  ${breakpoint.lessThan("sm")`
    margin: 20px 0 22px 0;
  `}
`;

const Divider = styled.span`
  color: "#333333";
  font-size: 12px;
  padding: 0 5px;
`;

type ItemTableRowProps = {
  header: string;
  items: (string | null)[];
  hidden?: boolean;
} & Partial<TableRowProps>;

export const ItemTableRow = ({ header, items, hidden }: ItemTableRowProps) => {
  const { isNewTheme } = useTheme();
  return (
    <>
      {!hidden && (
        <TableRow
          className={classNames({
            override: isNewTheme,
          })}
        >
          <TableHeader
            width={isNewTheme ? "208px" : "260px"}
            className={classNames({
              override: isNewTheme,
            })}
          >
            {header}
          </TableHeader>
          <TableData
            className={classNames({
              override: isNewTheme,
            })}
          >
            <List>
              {items
                .filter(v => v)
                .map((item, i) => (
                  <ListItem key={`${i + 1}`}>{item}</ListItem>
                ))}
            </List>
          </TableData>
        </TableRow>
      )}
    </>
  );
};

const createDetailsString = (
  details: {
    type: string;
    direction: string;
    width?: string;
    contactWidth?: string;
    connectingRoadWidthGroup?: string;
    connectingRoadContactWidthGroup?: string;
  }[] = []
) => details.map(d => `種別: ${d.type} 方向: ${d.direction} 幅員: ${d.width}ｍ 接面: ${d.contactWidth}ｍ`);

const createDetailsStringRow = (
  details: {
    type: string;
    direction: string;
    width?: string;
    contactWidth?: string;
    widthGroup?: string;
    contactWidthGroup?: string;
  }[] = [],
  isNewTheme?: boolean
) => {
  return details.map((detail, idx) => (
    <React.Fragment>
      <ItemTableRow header={`接道${idx + 1} : 種別`} items={[`${detail.type}`]} />
      <ItemTableRow header={`接道${idx + 1} : 方角`} items={[`${detail.direction}`]} />
      <ItemTableRow
        header={`接道${idx + 1} : ${isNewTheme ? "道路幅" : "幅員"}`}
        items={[`${isNewTheme === true ? detail.widthGroup : `${detail.width}ｍ`}`]}
      />
      <ItemTableRow
        header={`接道${idx + 1} : ${isNewTheme ? "間口" : "接面"}`}
        items={[`${isNewTheme === true ? detail.contactWidthGroup : `${detail.contactWidth}ｍ`}`]}
      />
    </React.Fragment>
  ));
};

const ifBlank = (value?: string | number) => defaultIfBlank(value, "-");

const ConfirmModal = ({
  open,
  onClose,
  onValuation,
  values: {
    propertyType,
    propertyName,
    propertyPref,
    propertyCity,
    propertyTown,
    propertyStreet,
    roomNumber,
    line,
    station,
    bus,
    walk,
    story,
    totalStory,
    undergroundStory,
    builtDateYear,
    builtDateMonth,
    houseSize,
    landSize,
    unitSize,
    balconyDirection,
    floorPlan,
    structure,
    connecting,
    lastName,
    lastNameKana,
    firstName,
    firstNameKana,
    tel,
    contactTime,
    mailAddress,
    postCode,
    pref,
    city,
    town,
    street,
    building,
    salePeriod,
    relation,
    saleReason,
    shopId,
    shopPrefId,
    referralSourceCd,
  },
}: ConfirmModalProps) => {
  const { isNewTheme, COLOR_THEME } = useTheme();
  const { PRIVACY_POLICY_LABEL_TEXT } = usePrivacyPolicyLabelText();

  const { current: range } = React.useContext(MediaQueryContext) || initMediaQueryStore("md");
  const {
    state: { privacyPolicyLink, termsLink, featuresObj, shopInfo, referralSource },
  } = React.useContext(ContractContext) || initContractStore(contract);
  const {
    state: { values },
  } = React.useContext(FormContext) || initFormStore(form);

  const CONTACT_PREFERENCE_MAIL: { has: boolean; extras: ContactPreferenceExtras } = {
    has: Boolean(featuresObj[Feature.CONTACT_PREFERENCE_MAIL]),
    extras: featuresObj[Feature.CONTACT_PREFERENCE_MAIL],
  };

  const isConnectingRoadInfoAutoEnabled = featuresObj[Feature.CONNECTING_ROAD_INFO_AUTO]?.enabled;
  const handleCanContactMail = useFormHandler("canContactMail");
  // CONTACT_PREFERENCE_MAIL.has が true かつ
  // CONTACT_PREFERENCE_MAIL.extras.position が "valuation_confirm_modal" の場合
  // 表示する
  const isMailCheckboxShow =
    CONTACT_PREFERENCE_MAIL.has &&
    CONTACT_PREFERENCE_MAIL.extras.position === CONTACT_PREFERENCE_POSITION.VALUATION_CONFIRM_MODAL;
  const isPositiveMessage =
    CONTACT_PREFERENCE_MAIL.has &&
    CONTACT_PREFERENCE_MAIL.extras.sentence &&
    CONTACT_PREFERENCE_MAIL.extras.sentence.use === "positive";
  const checkboxProps = {
    value: values.canContactMail ? "on" : "off",
    description:
      CONTACT_PREFERENCE_MAIL.has && CONTACT_PREFERENCE_MAIL.extras.description
        ? CONTACT_PREFERENCE_MAIL.extras.description
        : "",
    descriptionColor: "inherit",
    name: "canContactMail",
    onChange: (ev: React.ChangeEvent<HTMLInputElement>) => handleCanContactMail(ev.target.value === "off"),
    checked: Boolean(values.canContactMail),
    text: getSentence(CONTACT_PREFERENCE_MAIL.extras) || "今後当社からのメール配信を希望する",
    isCenter: true,
    checkboxColor: COLOR_THEME.extras?.mail_checkbox,
    extraMargins: { marginTop: "15px", marginBottom: "15px" },
  };
  if (!isPositiveMessage) {
    // negative, または null の場合
    checkboxProps.value = !values.canContactMail ? "on" : "off";
    checkboxProps.onChange = (ev: React.ChangeEvent<HTMLInputElement>) =>
      handleCanContactMail(ev.target.value !== "off");
    checkboxProps.checked = Boolean(!values.canContactMail);
    checkboxProps.text = getSentence(CONTACT_PREFERENCE_MAIL.extras) || "今後当社からのメール配信を希望しない";
  }

  const isMansion = PropertyType.MANSION === propertyType;
  const isHouse = PropertyType.HOUSE === propertyType;
  const isLand = PropertyType.LAND === propertyType;
  const hiddenLivingAddressComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);
  const hiddenPurchaseIntentionComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.PURCHASE_INTENTION);
  const hiddenKanaComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_KANA);
  const hiddenTelComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_TEL);
  const hiddenContactTime = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CONTACT_TIME);
  const hiddenTotalStory = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.TOTAL_STORY);
  const hiddenStructure = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.STRUCTURE);

  const detailsString = createDetailsString(connecting.details);

  const confirmDialogAnnotation = featuresObj[Feature.CONFIRM_DIALOG_ANNOTATION];
  const hasAnnotation = confirmDialogAnnotation && confirmDialogAnnotation.annotation;
  const isTrafficInfoAutoLinked = featuresObj[Feature.TRAFFIC_INFO_AUTO_LINK]?.enabled;
  const isShopFeatureEnabled = Boolean(featuresObj[Feature.SHOP_SETTINGS]?.enabled);
  const isReferralSource = Boolean(featuresObj[Feature.REFERRAL_SOURCE]?.enabled);
  const referralSourceText = featuresObj[Feature.REFERRAL_SOURCE]?.text ?? "";

  const renderHeader = () => {
    if (isNewTheme) {
      return (
        <React.Fragment>
          <StyledHeaderText>ご入力内容をご確認の上、「AI査定する」ボタンを押してください。</StyledHeaderText>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <HeaderText>ご入力内容の確認</HeaderText>
          <HeaderSubText>ご入力内容をご確認のうえ、「AI査定を行う」ボタンを押してください。</HeaderSubText>
        </React.Fragment>
      );
    }
  };

  const renderTermsAndConditions = (isBorder?: boolean) => {
    if (isNewTheme) {
      return (
        <React.Fragment>
          {isMailCheckboxShow && (
            <FooterCheckboxArea>
              <FormBlock.Row>
                <FormBlock.Checkbox
                  {...checkboxProps}
                  labelFontSize="14px"
                  labelfontWeight="700"
                  maxWidth="456px"
                  fontWeight="400"
                  fontSize="12px"
                  descriptionColor="#333333"
                  checkboxColor={{
                    text: "#333333",
                    checkbox: {
                      checked: "#0275ff",
                      unchecked: "#f2f2f2",
                    },
                    border: `2px solid ${COLOR_THEME.extras?.secondary}`,
                  }}
                  isCenter={true}
                  isNewTheme={isNewTheme}
                  isBorder={isBorder}
                />
              </FormBlock.Row>
            </FooterCheckboxArea>
          )}
          <PolicyArea>
            <StyledLink target="_blank" href={termsLink} color={COLOR_THEME.extras?.secondary}>
              {messages.terms}
            </StyledLink>
            <Divider>/</Divider>
            {privacyPolicyLink && (
              <StyledLink target="_blank" href={privacyPolicyLink} color={COLOR_THEME.extras?.secondary}>
                {PRIVACY_POLICY_LABEL_TEXT}
              </StyledLink>
            )}
          </PolicyArea>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <FooterText>利用規約、個人情報の取扱いについて</FooterText>
          <FooterTerms>
            利用規約については、
            <Link target="_blank" href={termsLink}>
              こちら
            </Link>
            をご確認ください。
          </FooterTerms>
          <FooterPolicy>
            お客さまの入力情報は、SSL通信により暗号化され守られております。
            <PcBreak />
            入力情報の取扱いについては、
            <Link target="_blank" href={privacyPolicyLink}>
              こちら
            </Link>
            をご確認ください。
          </FooterPolicy>
          {isMailCheckboxShow && (
            <FooterCheckboxArea>
              <FormBlock.Row>
                <FormBlock.Checkbox {...checkboxProps} />
              </FormBlock.Row>
            </FooterCheckboxArea>
          )}
        </React.Fragment>
      );
    }
  };
  const renderMiniTitle = (title: string, extraParams: string) => {
    if (isNewTheme) {
      return (
        <Title className="marginLeftZero" color={COLOR_THEME.extras?.secondary}>
          {title}
        </Title>
      );
    } else {
      return <MiniTitle margin={extraParams ? extraParams : ""}>{title}</MiniTitle>;
    }
  };

  const renderActionButton = () => {
    if (isNewTheme) {
      return (
        <React.Fragment>
          <ButtonWrapper className="marginTop15px">
            <StyledValuationExecButton background={COLOR_THEME.extras?.secondary} onClick={() => onValuation()}>
              <StyledValuationText>利用規約と{PRIVACY_POLICY_LABEL_TEXT}に同意して</StyledValuationText>
              <StyledValuationTextSub>AI査定する</StyledValuationTextSub>
            </StyledValuationExecButton>
          </ButtonWrapper>
          <StyledDiv>お客様の入力情報は、SSL通信により暗号化され保護されています。</StyledDiv>
          <ButtonWrapper>
            <StyledReturnButton onClick={() => onClose()}>入力画面に戻る</StyledReturnButton>
          </ButtonWrapper>
        </React.Fragment>
      );
    } else {
      return (
        <ButtonWrapper>
          <PrevButton value="入力画面に戻る" onClick={() => onClose()} />
          <ValuationExecButton onClick={() => onValuation()}>
            <NoWrap>利用規約と個人情報の取り扱いに同意し</NoWrap>
            <ValuationButtonText>AI査定を行う</ValuationButtonText>
          </ValuationExecButton>
        </ButtonWrapper>
      );
    }
  };

  const isMansionCssOverride = isMansion && (isNewTheme as boolean);
  const isMansionNotCssOverride = isMansion && !(isNewTheme as boolean);
  const isHouseCssOverride = isHouse && (isNewTheme as boolean);
  const isHouseCssNotOverride = isHouse && !(isNewTheme as boolean);

  const renderConnectingDetails = () => {
    if (!isNewTheme) {
      return <ItemTableRow header="接道情報" items={[`接道状況: ${connecting?.status}`, ...detailsString]} />;
    } else {
      return (
        <React.Fragment>
          <ItemTableRow header="接道状況" items={[`${connecting?.status}`]} />
          {createDetailsStringRow(connecting.details, isNewTheme).map(detail => detail)}
        </React.Fragment>
      );
    }
  };

  const getModalProps = () => {
    const modalProps = {
      open,
      width: range === "sm" ? "100vw" : "90vw",
      maxWidth: "85vh",
      maxHeight: "80vh",
      padding: "24px",
      top: range === "sm" ? "30px" : "50px",
      style: {} as React.CSSProperties,
    };
    if (isNewTheme) {
      modalProps.style.borderRadius = "10px";
      modalProps.style.overflowX = "hidden";

      if (range === "sm") {
        modalProps.style.margin = "10% 15px";
        modalProps.padding = "20px 30px";
      } else {
        modalProps.maxWidth = "77.7vw";
        modalProps.padding = "40px 60px";
      }
    }
    return modalProps;
  };

  const getNewTrafficInfo = () => {
    let lineAndStationInfo = `${line} ${station}`;
    if (lineAndStationInfo.length === 0) {
      lineAndStationInfo = "";
    }
    let walkAndBusInfo = null;
    if (walk || bus) {
      walkAndBusInfo = `徒歩 ${ifBlank(walk)}分 / バス ${ifBlank(bus)}分`;
    } else {
      walkAndBusInfo = "";
    }

    if (isNewTheme && isTrafficInfoAutoLinked !== true) {
      return <ItemTableRow header="最寄駅" items={[lineAndStationInfo, walkAndBusInfo]} />;
    }
  };

  const getOldTrafficInfo = () => {
    let lineAndStationInfo = `${line} ${station}`;
    if (lineAndStationInfo.length === 0) {
      lineAndStationInfo = "";
    }
    let walkAndBusInfo = null;
    if (walk || bus) {
      walkAndBusInfo = `駅から バス${ifBlank(bus)}分 徒歩${ifBlank(walk)}分`;
    } else {
      walkAndBusInfo = "";
    }
    if (!isNewTheme && isTrafficInfoAutoLinked !== true) {
      return <ItemTableRow header="交通アクセス" items={[lineAndStationInfo, walkAndBusInfo]} />;
    }
  };

  const getConnectingRoadInfo = () => {
    if (isNewTheme) {
      return (isHouse || isLand) && !isConnectingRoadInfoAutoEnabled;
    } else {
      return isHouse || isLand;
    }
  };

  return (
    <Modal {...getModalProps()}>
      <ModalBody>
        <ModalHeader
          className={classNames({
            override: isNewTheme,
          })}
        >
          {renderHeader()}
        </ModalHeader>
        <ModalBody
          className={classNames({
            override: isNewTheme,
          })}
        >
          {renderMiniTitle(isNewTheme ? "査定物件" : "物件情報", "")}
          <TableParent>
            <Table.Body>
              <ItemTableRow header="物件種別" items={[`${toLabel(propertyType)}`]} />
              {isMansion && <ItemTableRow header="マンション名" items={[`${propertyName}`]} />}
              <ItemTableRow
                header="所在地"
                items={[`${propertyPref}${propertyCity}${propertyTown}${!isMansion ? propertyStreet : ""}`]}
              />
              {getNewTrafficInfo()}
              {(isMansion || isHouse) && (
                <ItemTableRow
                  header="築年月"
                  items={[`${toIncludeJapaneseCalendar(`${builtDateYear}-${builtDateMonth}`)}`]}
                />
              )}
              {isMansion && (
                <ItemTableRow hidden={hiddenTotalStory} header="総階数" items={[`${ifBlank(totalStory)}`]} />
              )}
              {isMansion && (
                <ItemTableRow hidden={hiddenStructure} header="建物構造" items={[`${ifBlank(structure)}`]} />
              )}
              {getOldTrafficInfo()}
              {isMansionCssOverride && <ItemTableRow header="部屋番号" items={[`${ifBlank(roomNumber)}号室`]} />}
              {isMansionCssOverride && <ItemTableRow header="所在階" items={[`${ifBlank(story)}階`]} />}
              {isMansionCssOverride && <ItemTableRow header="専有面積" items={[`${ifBlank(unitSize)}㎡`]} />}
              {isMansionCssOverride && <ItemTableRow header="間取り" items={[`${ifBlank(floorPlan)}`]} />}
              {isMansionCssOverride && (
                <ItemTableRow header="バルコニー方向" items={[`${ifBlank(balconyDirection)}`]} />
              )}
              {isMansionNotCssOverride && (
                <ItemTableRow
                  header="お部屋"
                  items={[
                    `所在階: ${ifBlank(story)}階`,
                    `部屋番号: ${ifBlank(roomNumber)}号室`,
                    `専有面積: ${ifBlank(unitSize)}㎡`,
                    `間取り: ${ifBlank(floorPlan)}`,
                    `バルコニー方向: ${ifBlank(balconyDirection)}`,
                  ]}
                />
              )}
              {isHouseCssOverride && (
                <ItemTableRow header="階数" items={[`地上 ${totalStory}階 / 地下 ${undergroundStory}階`]} />
              )}
              {isHouseCssOverride && <ItemTableRow header="建物構造" items={[`${ifBlank(structure)}`]} />}
              {isHouseCssOverride && (
                <ItemTableRow
                  header={isNewTheme ? "建物延床面積" : "建物(延床)面積"}
                  items={[`${ifBlank(houseSize)}㎡`]}
                />
              )}
              {isHouseCssOverride && <ItemTableRow header="間取り" items={[`${floorPlan}`]} />}
              {(isHouse || isLand) && <ItemTableRow header="土地面積" items={[`${ifBlank(landSize)}㎡`]} />}
              {isHouseCssNotOverride && (
                <ItemTableRow
                  header="建物情報"
                  items={[
                    `地下: ${ifBlank(undergroundStory)}階`,
                    `地上: ${ifBlank(totalStory)}階`,
                    `建物構造: ${ifBlank(structure)}`,
                    `建物(延床)面積: ${ifBlank(houseSize)}㎡`,
                    `間取り: ${floorPlan}`,
                  ]}
                />
              )}
              {(isHouse || isLand) && getConnectingRoadInfo() && renderConnectingDetails()}
            </Table.Body>
          </TableParent>
          {(isNewTheme as boolean) && !hiddenPurchaseIntentionComponent && (
            <React.Fragment>
              {renderMiniTitle("ご検討状況", "20px 0 0")}
              <TableParent>
                <Table.Body>
                  <ItemTableRow
                    hidden={hiddenPurchaseIntentionComponent}
                    header="査定物件とのご関係"
                    items={[`${relation}`]}
                  />
                  <ItemTableRow
                    hidden={hiddenPurchaseIntentionComponent || !saleReason}
                    header="売却理由"
                    items={[`${saleReason}`]}
                  />
                  <ItemTableRow
                    hidden={hiddenPurchaseIntentionComponent}
                    header="売却ご希望時期"
                    items={[`${salePeriod}`]}
                  />
                </Table.Body>
              </TableParent>
            </React.Fragment>
          )}
          {renderMiniTitle(isNewTheme ? "ご連絡先" : "ご連絡先情報", "20px 0 0")}
          <TableParent>
            <Table.Body>
              <ItemTableRow header="お名前(漢字)" items={[`${lastName} ${firstName}`]} />
              <ItemTableRow
                hidden={hiddenKanaComponent}
                header="お名前(カナ)"
                items={[`${lastNameKana} ${firstNameKana}`]}
              />
              {(!isNewTheme as boolean) && <ItemTableRow header="メールアドレス" items={[`${mailAddress}`]} />}
              <ItemTableRow
                hidden={hiddenTelComponent}
                header={(isNewTheme as boolean) ? "電話番号" : "お電話番号"}
                items={[`${tel || "-"}`, !hiddenContactTime && tel ? `連絡希望時間帯: ${contactTime}` : null]}
              />
              {(isNewTheme as boolean) && <ItemTableRow header="メールアドレス" items={[`${mailAddress}`]} />}
              <ItemTableRow
                hidden={hiddenLivingAddressComponent}
                header={(isNewTheme as boolean) ? "ご住所" : "お住いの住所"}
                items={[
                  `${postCode ? `〒${postCode}` : ""} ${pref} ${city} ${town} ${street} ${defaultIfBlank(building)}`,
                ]}
              />
              {!(isNewTheme as boolean) && (
                <ItemTableRow
                  hidden={hiddenPurchaseIntentionComponent || !saleReason}
                  header="売却理由"
                  items={[`${saleReason}`]}
                />
              )}
              {!(isNewTheme as boolean) && (
                <ItemTableRow
                  hidden={hiddenPurchaseIntentionComponent}
                  header="売却希望時期"
                  items={[`${salePeriod}`]}
                />
              )}
              {!(isNewTheme as boolean) && (
                <ItemTableRow
                  hidden={hiddenPurchaseIntentionComponent}
                  header="あなたと査定物件の関係"
                  items={[`${relation}`]}
                />
              )}
              {(isNewTheme as boolean) && isShopFeatureEnabled && (
                <ItemTableRow
                  hidden={!isShopFeatureEnabled}
                  header="お問合せ店舗"
                  items={[`${shopPrefId} ${getShopById(shopInfo, Number(shopId))?.name}`]}
                />
              )}
              {(isNewTheme as boolean) && isReferralSource && (
                <ItemTableRow
                  hidden={!isReferralSource}
                  header={referralSourceText}
                  items={[`${getReferralSourceById(referralSource, Number(referralSourceCd))?.referralSourceName}`]}
                />
              )}
            </Table.Body>
          </TableParent>
          {hasAnnotation && (
            <Annotation
              className={classNames({
                override: isNewTheme,
              })}
            >
              {confirmDialogAnnotation.annotation}
            </Annotation>
          )}
        </ModalBody>
        {isNewTheme ? (
          <ModalFooter2>
            {renderTermsAndConditions(true)}
            {renderActionButton()}
          </ModalFooter2>
        ) : (
          <ModalFooter>
            {renderTermsAndConditions()}
            {renderActionButton()}
          </ModalFooter>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ConfirmModal;
